import * as angular from 'angular';
import { Growl } from 'angular/custom/services/growl.service';
import { config, reactShell } from 'angular/utils/config';
import { StorageLocationResource } from 'app/_resources/storage-location.resource';
import { ErrorHandler } from 'app/_services/error-handler.factory';
import { StorageAllocationResource } from 'app/storage-offer/storage-allocation.resource';
import { StorageOfferResource } from 'app/storage-offer/storage-offer.resource';
import { StorageOffersService } from 'app/storage-offer/storage-offers.service';

class StorageOfferDetailsComponentCtrl implements ng.IController {
    public offerId: number;
    public offer: StorageOfferDto;
    public offerBids: StorageOfferBidDto[] = [];
    public offerBidsPreviousRound: StorageOfferBidDto[] = [];
    public offerBidsCopy: StorageOfferBidDto[] = [];
    public tradedQuantities: StorageTradedQuantityDto;
    public loadingError: boolean = false;
    public errorDelete: boolean = false;
    public loadingData: boolean = true;
    public allocationEditModeActive = false;
    public allocationEditDisabled = false;
    public allocationsVisible = false;
    public countdownDateTime;
    public errMsg: string;
    public errorDeleteMessage: string;
    public isSbu: boolean;
    public loggedInUser: LoginResult;
    public stageConfig = config;
    public reactShell = reactShell;
    public editBidId: string;

    public allocationAcceptanceLoading = false;
    public allocationRejectionLoading = false;
    public allocationStartLoading = false;

    // because due to angular scoping an inline definition does not work
    public hideProductComponents = {};
    public editReservePriceActive: boolean[];

    public ssoId: number;

    constructor(
        private $window: ng.IWindowService,
        private growl: Growl,
        private $location: ng.ILocationService,
        private $anchorScroll: ng.IAnchorScrollService,
        private $route: ng.route.IRouteService,
        private $timeout: ng.ITimeoutService,
        private $q: ng.IQService,
        private storageOfferResource: StorageOfferResource,
        private storageAllocationResource: StorageAllocationResource,
        private storageLocationResource: StorageLocationResource,
        private errorHandler: ErrorHandler,
        private storageOffersService: StorageOffersService
    ) {}

    public $onInit(): void {
        let getOfferPromise = this.storageOfferResource.get(
            { id: this.offerId },
            (res: StorageOfferDto) => {
                this.offer = res;
                this.isSbu = this.offer.productType === 'SBU';
                this.ssoId = res.ssoId;

                this.countdownDateTime =
                    StorageOfferDetailsComponentCtrl.startOfNextPhase(
                        this.offer.state,
                        {
                            offerPublication: this.offer.publicationDateAndTime,
                            offerStart: this.offer.offerStart,
                            offerClose: this.offer.offerClose,
                            secondRoundOfferPublication:
                                this.offer.secondRoundPublicationDateAndTime,
                            secondRoundOfferStart:
                                this.offer.secondRoundOfferStart,
                            secondRoundOfferClose:
                                this.offer.secondRoundOfferClose
                        },
                        this.offer.currentRound
                    );

                if (this.offer.storageLocationRefId > 0) {
                    this.storageLocationResource
                        .get(
                            { id: this.offer.storageLocationRefId },
                            (location: StorageLocationDto) => {
                                this.offer.storageLocationName = location.name;
                            }
                        )
                        .$promise.catch(() =>
                            this.growl.show(
                                'error reading location ' +
                                    this.offer.storageLocationRefId,
                                'warning'
                            )
                        );
                }

                this.refreshAllocationVisibleFlag();
                this.loadingError = false;
            },
            (err: any) => {
                const errorMessage =
                    err && err.data && err.data.message
                        ? err.data.message
                        : 'An unknown error occurred. Please, check back later.';
                this.errMsg = errorMessage;
                this.loadingError = true;
            }
        ).$promise;

        let getTradedQuantitiesPromise = this.getTradedQuantities();

        this.$q
            .all([getOfferPromise, getTradedQuantitiesPromise])
            .finally(() => {
                this.loadingData = false;
                if (this.$location.search().editBidId) {
                    this.editBidId = this.$location.search().editBidId;
                }
            });
    }

    private getTradedQuantities() {
        return this.storageOfferResource.getTradedQuantity(
            { offerId: this.offerId },
            (res: StorageTradedQuantityDto) => {
                this.tradedQuantities = res;
            },
            (err: any) => {
                this.errorHandler(err);
            }
        ).$promise;
    }

    public goBack() {
        this.$window.history.back();
    }

    public static startOfNextPhase(state, triggerTime, currentRound) {
        if (state === 'DRAFT') {
            return triggerTime.offerPublication;
        } else if (state === 'PUBLISHED') {
            return currentRound === 1
                ? triggerTime.offerStart
                : triggerTime.secondRoundOfferStart;
        } else if (state === 'OPEN') {
            return currentRound === 1
                ? triggerTime.offerClose
                : triggerTime.secondRoundOfferClose;
        } else if (state === 'WAITING_FOR_NEXT_ROUND') {
            return !!triggerTime.secondRoundOfferPublication
                ? triggerTime.secondRoundOfferPublication
                : triggerTime.secondRoundOfferStart;
        }

        return null;
    }

    public onSuccessfullyPlacedBid(): void {
        this.getBids();
        this.scrollTo('your-company-bids');
        if (this.offer.state === 'PUBLISHED') {
            this.growl.show(
                `Your pre-bid has been created successfully.`,
                'success'
            );
        } else {
            this.growl.show(
                `Your bid has been placed successfully.`,
                'success'
            );
        }
    }

    public isOwnOffer(): boolean {
        return (
            this.loggedInUser &&
            this.offer.ssoId === this.loggedInUser.referenceId
        );
    }

    private getBids(): ng.IPromise<any> {
        //fetch the bids for this storage offer
        return this.storageOfferResource.getBids(
            { id: this.offer.id },
            (res: StorageOfferBidDto[]) => {
                if (
                    this.offer.currentRound !== 2 &&
                    this.offer.state !== 'WAITING_FOR_NEXT_ROUND'
                ) {
                    this.offerBids = res;
                } else {
                    this.offerBids = [];
                    this.offerBidsPreviousRound = [];
                    angular.forEach(res, (offerBid) => {
                        if (offerBid.roundNumber === 0) {
                            this.offerBidsPreviousRound.push(offerBid);
                        } else {
                            this.offerBids.push(offerBid);
                        }
                    });
                }

                if (
                    this.offer.allocationMethod ==
                    'NO_ALLOCATION_2_BIDDING_ROUNDS'
                ) {
                    angular.forEach(this.offerBids, (offerBid) => {
                        angular.forEach(offerBid.productBids, (productBid) => {
                            if (!productBid.productBidAllocation) {
                                productBid.productBidAllocation = {
                                    storageProductId:
                                        productBid.storageProductId,
                                    allocatedQuantityValue: 0,
                                    priceValue: angular.copy(
                                        productBid.priceAmount
                                    )
                                };
                            }
                        });
                    });
                }
            },
            (err: any) => {
                this.errorHandler(err);
            }
        ).$promise;
    }

    private refreshAllocationVisibleFlag() {
        this.allocationsVisible =
            this.offer.state === 'FINISHED' &&
            (this.offer.allocationState === 'ALLOCATIONS_ACCEPTED' ||
                this.offer.allocationState === 'ALLOCATIONS_UNCONFIRMED') &&
            this.loggedInUser &&
            this.loggedInUser.role === 'SSO_ADMIN';
    }

    public deleteOffer(offer: StorageOfferDto): void {
        this.storageOfferResource.delete(
            { id: offer.id },
            () => {
                this.storageOffersService.offerDeletedSuccessfully = true;
                this.goBack();
            },
            (err) => {
                this.errorDelete = true;
                const errorMessage =
                    err && err.data && err.data.message
                        ? err.data.message
                        : 'An unknown error occurred. Please, check back later.';
                this.errorDeleteMessage = errorMessage;
                this.scrollTo('offer-details-top');
            }
        );
    }

    public acceptAllocation() {
        this.allocationAcceptanceLoading = true;
        this.sendAllocationAcceptance(true)
            .then((alloc) => {
                this.offer.allocationState = 'ALLOCATIONS_ACCEPTED';
                this.growl.show(
                    'Allocation successful. A notification is sent to the shippers.',
                    'success'
                );
                this.allocationAcceptanceLoading = false;
            })
            .catch((err) => {
                this.errorHandler(err);
                this.allocationAcceptanceLoading = false;
            });
    }

    public rejectAllocation() {
        this.allocationRejectionLoading = true;
        this.sendAllocationAcceptance(false)
            .then((alloc) => {
                this.offer.allocationState = 'ALLOCATIONS_REJECTED';
                this.growl.show('Allocation was rejected', 'success');
                this.allocationRejectionLoading = false;
            })
            .catch((err) => {
                this.errorHandler(err);
                this.allocationRejectionLoading = false;
            });
    }

    private sendAllocationAcceptance(
        acceptAllocation: boolean
    ): ng.IPromise<StorageAllocationAcceptanceDto> {
        const acceptance: StorageAllocationAcceptanceDto = {
            offerId: this.offer.id,
            acceptAllocation
        };
        return this.storageOfferResource.sendOfferAllocationAcceptance(
            { id: this.offer.id },
            acceptance
        ).$promise as ng.IPromise<StorageAllocationAcceptanceDto>;
    }

    public startAllocation() {
        this.allocationStartLoading = true;
        this.growl.show(
            'Your allocation is running. Please wait until your capacity was allocated.',
            'info'
        );
        this.storageAllocationResource
            .startAllocation(
                { id: this.offer.id },
                () => {
                    this.getTradedQuantities();
                    this.getBids();
                    this.storageOfferResource.get(
                        { id: this.offerId },
                        (res: StorageOfferDto) => {
                            this.offer = res;
                            this.refreshAllocationVisibleFlag();
                            if (this.offerBids.length > 0) {
                                this.growl.show(
                                    'Your capacity has been allocated successfully. Please download the .csv-file to see the results.',
                                    'success'
                                );
                            } else {
                                this.growl.show(
                                    'Your capacity has been allocated successfully. ',
                                    'success'
                                );
                            }
                        },
                        (err) => {
                            this.errorHandler(err);
                        }
                    );
                },
                (err) => {
                    this.errorHandler(err);
                }
            )
            .$promise.finally(() => {
                this.allocationStartLoading = false;
            });
    }

    public updateReservePrice(
        productId: number,
        index: number
    ): ng.IPromise<any> {
        let newReservePrice: StorageBiddingPriceDto;
        if (!!this.offer.products[index].reservePrice.value) {
            newReservePrice = {
                value: this.offer.products[index].reservePrice.value,
                unit: this.offer.products[index].biddingPriceUnit
            };
            this.offer.products[index].reservePrice.unit =
                this.offer.products[index].biddingPriceUnit;
        } else {
            newReservePrice = null;
        }
        return this.storageOfferResource.updateReservePrice(
            {
                offerId: this.offer.id,
                id: productId
            },
            newReservePrice,
            () => {
                this.editReservePriceActive[index] = false;
            }
        ).$promise;
    }

    public scrollToOnAction(anchorId: string, action: string): void {
        if (action !== this.$route.current.params.action) {
            return;
        }

        this.scrollTo(anchorId);
    }

    private scrollTo(anchorId: string) {
        this.$timeout(() => {
            let old = this.$location.hash();
            this.$location.hash(anchorId);
            this.$anchorScroll();
            this.$location.hash(old);
        });
    }

    public toggleEdit() {
        this.allocationEditModeActive = !this.allocationEditModeActive;

        //Copy bids for undo purposes
        if (this.allocationEditModeActive) {
            this.offerBidsCopy = angular.copy(this.offerBids);
        }
    }

    public cancelEditOfferBids() {
        //undo edited offers
        this.toggleEdit();
        this.offerBids = angular.copy(this.offerBidsCopy);
    }

    public submitEditOfferBids() {
        this.allocationEditDisabled = true;
        this.storageAllocationResource
            .bidAllocatedQuantity(
                { id: this.offer.id },
                this.offerBids,
                () => {
                    this.toggleEdit();
                    this.getTradedQuantities();
                    this.growl.show(
                        'Allocation value(s) updated successfully',
                        'success'
                    );
                },
                (err: any) => {
                    this.errorHandler(err);
                }
            )
            .$promise.finally(() => {
                this.allocationEditDisabled = false;
            });
    }
}

angular.module('app').component('pkpStorageOfferDetails', {
    controller: StorageOfferDetailsComponentCtrl,
    template: require('app/storage-offer/storage-offer-details.component.html'),
    bindings: {
        offerId: '<?',
        loggedInUser: '<'
    }
});
